import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, XCircle, ArrowRight, Phone } from "lucide-react";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const PATH = "/colorado-vs-national-agencies";
const TITLE = "Colorado SEO Agency vs. National SEO Firms | Honest Comparison";
const DESCRIPTION =
  "How an owner-led, Colorado-only SEO agency compares to national SEO firms — pricing, contracts, communication, ranking results, and Map Pack work. Plain comparison, no spin.";

const ROWS: Array<{ label: string; cwi: string; national: string }> = [
  {
    label: "Who actually does the work",
    cwi: "The owner — Chris Holcombe — does strategy, writes the pages, tunes the GBP, and answers your calls.",
    national: "An offshore production team you'll never meet, coordinated by an account manager who rotates every 6–12 months.",
  },
  {
    label: "Local market knowledge",
    cwi: "Lives and works in Colorado. Knows Colorado Springs, Denver, Fort Collins, Boulder, Pueblo neighborhoods firsthand.",
    national: "Generic templates applied to every market. No knowledge of how Coloradans actually search or buy.",
  },
  {
    label: "Contracts",
    cwi: "Month-to-month, cancel anytime. Earn the next month with last month's results.",
    national: "6 to 24-month contracts with steep early-termination fees and auto-renewal clauses.",
  },
  {
    label: "Pricing transparency",
    cwi: "Prices published on the site. Growth Marketing package is $1,650/month. No 'custom proposals' to hide markup.",
    national: "'Schedule a call for pricing.' Sticker shock and three-tier upsells after a 45-minute discovery call.",
  },
  {
    label: "Reporting honesty",
    cwi: "Real ranking data, real Map Pack movement, real call tracking. We send a Loom video walking you through it.",
    national: "Vanity dashboards full of 'impressions' and 'keywords ranking somewhere in the top 100' — the metrics that don't matter.",
  },
  {
    label: "Map Pack and GBP work",
    cwi: "Hands-on weekly GBP posts, geo-tagged photos, review velocity, neighborhood Q&A — the work that moves the Map Pack.",
    national: "GBP is a checkbox on the onboarding form. Posts (if any) are AI-generated and never reference your city.",
  },
  {
    label: "Content quality",
    cwi: "Pages written by someone who has actually run a small business in Colorado. Sound like a human, target real local intent.",
    national: "AI-generated content with city names swapped in. 'Best HVAC company in [INSERT CITY]' templates at scale.",
  },
  {
    label: "Response time",
    cwi: "Direct call or text to the owner. Usually answered same business day. No ticket queue.",
    national: "Email-only support routed through an offshore queue. 24–72 hour response SLA buried in the contract.",
  },
  {
    label: "Total client count per strategist",
    cwi: "We cap our roster intentionally so every account gets owner attention.",
    national: "Account managers handle 40–80 clients. Strategy gets templated; nobody actually thinks about your business.",
  },
  {
    label: "Website ownership",
    cwi: "You own the site, the domain, the hosting login, the analytics. Walk away with everything if you ever leave.",
    national: "Site is on the agency's proprietary platform. Cancel and you lose the site — it's a hostage strategy.",
  },
];

const FAQS = [
  {
    q: "Aren't some 'national' SEO firms actually based in Colorado?",
    a: "Yes — a few national agencies are headquartered in Denver. That doesn't make them local. Their service model, account management, and content production are still designed for scale across all 50 states. 'Headquarters in Colorado' isn't the same as 'works in Colorado markets.'",
  },
  {
    q: "Aren't national agencies cheaper because of scale?",
    a: "Almost never. National agencies typically run $3,500–$15,000/month for what they call a 'mid-market' SEO program. Our Growth Marketing package is $1,650/month and includes SEO, Google Maps, GEO AI, and social. The 'scale' goes into their margins, not your invoice.",
  },
  {
    q: "What if I need a huge content output that a small agency can't deliver?",
    a: "If you genuinely need 100+ new pages per month across multiple markets, a large content shop may be a better fit. But most Colorado small and mid-sized businesses need 4–12 well-written, intent-targeted pages per month — and that's exactly the volume an owner-led shop produces with deeper care than a content mill.",
  },
  {
    q: "How do I evaluate a national firm if I'm already considering one?",
    a: "Ask three questions: (1) Who specifically writes my pages and what city do they live in? (2) What's the cancellation clause and are there early-termination fees? (3) Will I own my website and domain, or is it on your platform? The answers will tell you everything you need to know.",
  },
  {
    q: "Do you ever recommend a national firm over yourself?",
    a: "Yes — when a business has a national footprint (50-state e-commerce, multi-state franchise) and needs scale we can't match. But for any Colorado-based business serving Colorado customers, an owner-led local shop almost always produces better results per dollar.",
  },
];

export const Route = createFileRoute("/colorado-vs-national-agencies")({
  head: () => ({
    meta: [
        { name: "description", content: "Honest comparison of hiring a local Colorado marketing agency vs. a national chain. Pricing, accountability, and results owners actually see." },
        { title: "Local Colorado Agency vs. National Marketing Firms" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://coloradowebimpressions.com${PATH}` },
      ...ogMetaTags(ogImageFor({ service: "seo" })),
      ...twitterTextTags({ title: TITLE, description: DESCRIPTION }),
    ],
    links: [{ rel: "canonical", href: `https://coloradowebimpressions.com${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `https://coloradowebimpressions.com${PATH}#article`,
          headline: TITLE,
          description: DESCRIPTION,
          url: `https://coloradowebimpressions.com${PATH}`,
          image: "https://coloradowebimpressions.com/logo.png",
          datePublished: "2025-09-01",
          dateModified: "2026-05-25",
          author: { "@id": "https://coloradowebimpressions.com/meet-chris#chris-heidlebaugh" },
          publisher: { "@id": "https://coloradowebimpressions.com/#org" },
          mainEntityOfPage: `https://coloradowebimpressions.com${PATH}`,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Comparisons", path: "/honest-answers" },
        { name: "Colorado vs. National Agencies", path: PATH },
      ]),
    ],
  }),
  component: ComparisonPage,
});

function ComparisonPage() {
  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="bg-gradient-to-b from-brand-black to-background py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Honest comparison
          </span>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Colorado SEO agency vs. <span className="text-gradient-gold">national SEO firms</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            A plain comparison — pricing, contracts, who does the work, what kind of results
            you can actually expect — between an owner-led Colorado-only agency and the
            national SEO model. No spin.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/free-audit"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a free Colorado audit
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-4 text-base font-semibold text-foreground transition-colors hover:bg-muted"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-20 lg:py-28" aria-labelledby="comparison-heading">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 id="comparison-heading" className="text-3xl font-extrabold text-foreground sm:text-4xl">
              How we actually compare
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Ten of the things that matter most when picking an SEO partner — and how an
              owner-led Colorado shop stacks up against the national model.
            </p>
          </div>

          <div className="mt-12 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-3">
              <div className="bg-muted/40 p-5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground md:p-6">
                What matters
              </div>
              <div className="bg-brand-gold/10 p-5 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep md:p-6">
                Colorado Web Impressions
              </div>
              <div className="bg-muted/40 p-5 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground md:p-6">
                National SEO firms
              </div>
              {ROWS.map((row) => (
                <div key={row.label} className="contents">
                  <div className="bg-card p-5 text-sm font-semibold text-foreground md:p-6">
                    {row.label}
                  </div>
                  <div className="flex items-start gap-2 bg-card p-5 text-sm text-foreground md:p-6">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                    <span>{row.cwi}</span>
                  </div>
                  <div className="flex items-start gap-2 bg-card p-5 text-sm text-muted-foreground md:p-6">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
                    <span>{row.national}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* When national IS better */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            When a national firm <em>is</em> the right call
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            We don't think national agencies are evil — they're built for a different job.
            Hire one when you genuinely need:
          </p>
          <ul className="mt-6 space-y-3 text-base text-foreground">
            {[
              "A 50-state e-commerce or SaaS rollout where you need 100+ pages per month",
              "Multi-state franchise SEO across dozens of locations",
              "Enterprise-level technical SEO on a site with 50K+ URLs",
              "A national PR + link-building campaign with a six-figure budget",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-brand-gold-deep" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-lg text-muted-foreground">
            For any Colorado-based business serving Colorado customers, an owner-led local
            shop almost always produces better results per dollar — because the work is
            done by someone who actually understands your market.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 id="faq-heading" className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Frequently asked questions
          </h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border bg-card p-5 transition-colors hover:bg-muted/40"
              >
                <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                  {f.q}
                </summary>
                <p className="mt-3 text-base text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-black py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want a real comparison for <span className="text-gradient-gold">your</span> business?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Send us a current proposal from a national firm and we'll walk you through what
            you're actually paying for — and what we'd do differently for less.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/free-audit"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a free Colorado audit
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-4 text-base font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Talk to the owner
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
